import { useMemo } from 'react';

import { useMerchant } from '../../../contexts/MerchantContext';
import { useGetCredentialsQuery, useGetMerchantQuery } from '../../../store/modules/merchant/api';
import { getSquarespaceStoreLink, type SquarespaceStoreLink } from './squarespaceStore';

/** `link` stays undefined until both reads succeed, so a failed read never looks unconnected. */
export const useSquarespaceStoreLink = (): {
  link: SquarespaceStoreLink | undefined;
  isError: boolean;
} => {
  const { merchantId } = useMerchant();
  const {
    data: merchant,
    isSuccess: isMerchantLoaded,
    isError: isMerchantError,
  } = useGetMerchantQuery(merchantId, { skip: !merchantId });
  const {
    data: credentials,
    isSuccess: areCredentialsLoaded,
    isError: isCredentialsError,
  } = useGetCredentialsQuery({ merchantId, type: ['ecommerce'] }, { skip: !merchantId });

  const isLoaded = isMerchantLoaded && areCredentialsLoaded;
  const merchantExternalPlatformId = merchant?.externalPlatformId;
  // Memoized so the publish effect does not rerun every render.
  const link = useMemo(
    () => (isLoaded ? getSquarespaceStoreLink(credentials, merchantExternalPlatformId) : undefined),
    [isLoaded, credentials, merchantExternalPlatformId],
  );

  return { link, isError: isMerchantError || isCredentialsError };
};
